import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { useUserStore } from '@/store/userStore';
import { computed, Ref, ref } from 'vue';
import { createResourceUri } from '@/utils/links';
import { AgentURI } from '@/types/agent';
import { Theme } from '@/types/general';
import { YapListItem } from '@/components/YapList.vue';
import { useProfileStore } from '@/store/profileStore';
import { useI18n } from '@/plugins/i18n';
import { BuddyReferralCode } from '@/components/suggestion/BuddySuggestion.vue';
import { NaturalPersonURI } from '@/types/naturalPerson';
import { usePartnershipsStore } from '@/store/partnershipsStore';

export enum SuggestionType {
  Ebill = 'ebill',
  EbillBusiness = 'ebill_business',
  ReleaseNotes = 'release_notes',
  MonthStats = 'month_stats',
  Wise = 'wise',
  Yapini = 'yapini',
  Vt3a = 'vt3a',
  Cashback = 'cashback',
  AbacusPromo = 'abacus_promo',
  Welcome = 'welcome',
  Pay4value = 'pay4value',
  AppleWallet = 'apple_wallet',
  GoogleWallet = 'google_wallet',
  SamsungPay = 'samsung_pay',
  AvoidFees = 'avoid_fees',
  CardComing = 'card_coming',
  ThankYou = 'thank_you',
  BuddyProgram = 'buddy_program',
  SunrisePromo = 'sunrise_promo',
  SmeOffering = 'sme_offering'
}

export enum SuggestionCommand {
  Enable = 'enable',
  Disable = 'disable'
}

export interface Suggestion {
  triggerUid: string;
  type: SuggestionType;
  titleTag: string;
  hintTag: string;
  hasDetails: boolean;
  isRemovable: boolean;
  isRemoved: boolean;
  isHighlighted: boolean;
  darkModeImageURI: string;
  lightModeImageURI: string;
  title: string;
  priority: number;
}

export const suggestionsRefdataCollection = 'api.event';

export const useSuggestionsStore = defineStore('suggestions', () => {
  const { t } = useI18n();
  const contextSwitchStore = useContextSwitchStore();
  const userStore = useUserStore();
  const profileStore = useProfileStore();
  // STATES
  const suggestionsInContext: Ref<{ [key: string]: Suggestion[] }> = ref({});
  const suggestionsShowHidden: Ref<boolean> = ref(false);
  const referralCode: Ref<BuddyReferralCode | undefined> = ref(undefined);

  // GETTERS
  function getSuggestionsShowHidden() {
    return suggestionsShowHidden.value;
  }

  function getReferralCode() {
    return referralCode.value;
  }

  const sortedSuggestions = computed(() => {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid && suggestionsInContext.value[ctxUid]) {
      return [...suggestionsInContext.value[ctxUid]].sort((a, b) => {
        if (a.isHighlighted && !b.isHighlighted) return -1;
        if (!a.isHighlighted && b.isHighlighted) return 1;
        return a.priority - b.priority;
      });
    }
    return [];
  });

  function getSuggestionAsYapListItem(suggestion: Suggestion): YapListItem {
    return {
      key: suggestion.type,
      title: t(`${suggestionsRefdataCollection}.${suggestion.titleTag}`),
      subtitle: t(`${suggestionsRefdataCollection}.${suggestion.hintTag}`),
      imageUri: profileStore.getTheme === Theme.DARK_THEME ? suggestion.darkModeImageURI : suggestion.lightModeImageURI,
      imageAltText: `${suggestion.type} icon`,
      extraData: {
        banner: getSuggestionBanner(suggestion.type),
        triggerUid: suggestion.triggerUid,
        hasDetails: suggestion.hasDetails,
        isRemovable: suggestion.isRemovable,
        isRemoved: suggestion.isRemoved,
        isHighlighted: suggestion.isHighlighted,
        priority: suggestion.priority
      },
      displayCondition: !suggestion.isRemoved
    };
  }

  function getSortedSuggestionsAsYapList(showHidden?: boolean): YapListItem[] {
    return sortedSuggestions.value
      .map((suggestion: Suggestion) => {
        const item = getSuggestionAsYapListItem(suggestion);
        item.displayCondition = showHidden === true ? showHidden : !suggestion.isRemoved;
        return item;
      })
      .filter((suggestion: YapListItem) => suggestion.displayCondition === undefined || suggestion.displayCondition);
  }

  function getSuggestionByType(type: SuggestionType) {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid && suggestionsInContext.value[ctxUid]) {
      return suggestionsInContext.value[ctxUid].find((s) => s.type === type);
    }
    return undefined;
  }

  function getYapListSuggestionByType(type: SuggestionType, showHidden?: boolean) {
    const ctxUid = contextSwitchStore.currentContextUid;
    if (ctxUid && suggestionsInContext.value[ctxUid]) {
      return getSortedSuggestionsAsYapList(showHidden).find((s) => s.key === type);
    }
    return undefined;
  }

  function hasActivateEbillSuggestion() {
    return (
      !!contextSwitchStore.currentContextUid &&
      ((contextSwitchStore.isPrivateContext && !userStore.hasEbillActivated) ||
        !!suggestionsInContext.value[contextSwitchStore.currentContextUid]?.filter((suggestion) =>
          [SuggestionType.EbillBusiness].includes(suggestion.type)
        ).length)
    );
  }

  function hasRemovedSuggestions() {
    return (
      !!contextSwitchStore.currentContextUid &&
      !!suggestionsInContext.value[contextSwitchStore.currentContextUid]?.filter((suggestion) => suggestion.isRemoved)
        .length
    );
  }

  function getSuggestionBanner(type: SuggestionType) {
    if (type === SuggestionType.Ebill || type === SuggestionType.EbillBusiness) return '/images/ebill/banner.jpg';
    if (type === SuggestionType.Wise) return '/images/wise/banner.jpg';
    return '/images/suggestions/banner.jpg';
  }

  // ACTIONS
  function $reset() {
    suggestionsInContext.value = {};
    suggestionsShowHidden.value = false;
    referralCode.value = undefined;
  }

  async function loadSuggestionsForCurrentContext() {
    const ctxUid = contextSwitchStore.getCurrentContext()?.uid;
    if (!ctxUid) {
      console.warn('no context yet...');
      return;
    }
    loadSuggestionsForContext(ctxUid);
  }
  async function loadSuggestionsForContext(ctxUid: string) {
    if (!ctxUid) {
      console.warn('no context provided...');
      return;
    }

    if (usePartnershipsStore().isRequestorBootstrapperForCurrentPartnership) {
      console.warn('unable to load suggestions for context');
      return;
    }

    const uri = createResourceUri(AgentURI.AgentV2, { uid: ctxUid, resource: AgentURI.Suggestions });

    if (uri) {
      const response = await apiGw.get(uri);

      suggestionsInContext.value[ctxUid] = response.data;
    }
  }

  async function updateHiddenSuggestion(type: SuggestionType, cmd: SuggestionCommand) {
    const suggestion = getSuggestionByType(type);

    if (suggestion) {
      try {
        await apiGw.post(
          createResourceUri(AgentURI.AgentV2, {
            uid: contextSwitchStore.currentContextUid,
            resource: `${AgentURI.Suggestions}/${suggestion.triggerUid}/${cmd}`
          })
        );
      } catch (error) {
        console.error('Something went wrong when doing command on suggestion', error);
      }
    }
  }

  async function loadReferralCode() {
    if (contextSwitchStore.currentContextUid) {
      try {
        const res = await apiGw.get(
          createResourceUri(NaturalPersonURI.Base, {
            uid: contextSwitchStore.currentContextUid,
            resource: NaturalPersonURI.ReferralCode
          })
        );
        referralCode.value = res.data;
      } catch (error) {
        console.error('Something went wrong when getting referral code', error);
      }
    }
  }

  function updateSuggestionsShowHidden(value: boolean) {
    suggestionsShowHidden.value = value;
  }

  function removeSuggestionTypeInCurrentContext(type: SuggestionType) {
    const ctxUid = contextSwitchStore.getCurrentContext()?.uid;
    if (!ctxUid) {
      console.warn('no context yet...');
      return;
    }
    removeSuggestionTypeInContext(type, ctxUid);
  }

  function removeSuggestionTypeInContext(type: SuggestionType, ctxUid: string) {
    suggestionsInContext.value[ctxUid] = suggestionsInContext.value[ctxUid].filter((s) => s.type !== type);
  }

  return {
    // STATES
    suggestionsInContext,
    suggestionsShowHidden,
    getReferralCode,
    // GETTERS
    getSuggestionsShowHidden,
    sortedSuggestions,
    getSuggestionAsYapListItem,
    getSortedSuggestionsAsYapList,
    getSuggestionByType,
    getYapListSuggestionByType,
    hasActivateEbillSuggestion,
    hasRemovedSuggestions,
    // ACTIONS
    $reset,
    loadSuggestionsForContext,
    updateHiddenSuggestion,
    updateSuggestionsShowHidden,
    loadReferralCode,
    removeSuggestionTypeInContext,
    loadSuggestionsForCurrentContext,
    removeSuggestionTypeInCurrentContext
  };
});
